import { useTranslation } from "react-i18next";
import type { Run, RunStep } from "~/api/types";
import { compactTokens, formatDuration } from "~/features/automation/format";
import { MessageMarkdown } from "~/features/conversations/MessageMarkdown";
import { sumStepTokens } from "~/features/runs/runDerive";
import { ActionRequired } from "~/foundation/components/ActionRequired";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";

/**
 * The run's answer, its failure, and what it cost.
 *
 * The token line names how many steps were actually measured. A run whose executor reports no
 * usage (Copilot has no transcript) would otherwise show a confident "0 tokens", which is a
 * measurement claim nobody made.
 */
export function RunResultTab({
  run,
  steps,
  onPublish,
}: {
  run: Run;
  steps: RunStep[];
  onPublish: () => void;
}) {
  const { t } = useTranslation();
  const tokens = sumStepTokens(steps);

  if (!run.result && !run.error) {
    return <Empty message={t("This run has produced no result yet.")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {run.error ? <ActionRequired error={run.error} /> : null}

      {run.result ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-small text-text-tertiary">{t("Result")}</span>
            <Button size="sm" variant="neutral" onClick={onPublish}>
              {t("Save to KnowIt…")}
            </Button>
          </div>
          <div className="min-w-0 rounded-md border border-divider bg-bg p-4 text-body">
            <MessageMarkdown content={run.result} />
          </div>
        </div>
      ) : null}

      <dl className="flex flex-wrap gap-6">
        <div className="flex flex-col gap-0.5">
          <dt className="text-small text-text-tertiary">{t("Duration")}</dt>
          <dd className="text-body text-text tabular-nums">{formatDuration(run.durationMs, t)}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-small text-text-tertiary">{t("Tokens")}</dt>
          <dd className="text-body text-text tabular-nums">
            {tokens.measured === 0
              ? t("not measured")
              : t("{{total}} ({{in}} in / {{out}} out)", {
                  total: compactTokens(tokens.total),
                  in: compactTokens(tokens.input),
                  out: compactTokens(tokens.output),
                })}
            {tokens.measured > 0 && tokens.measured < steps.length ? (
              <span className="ms-1 text-small text-text-tertiary">
                {t("· measured on {{measured}} of {{total}} steps", {
                  measured: tokens.measured,
                  total: steps.length,
                })}
              </span>
            ) : null}
          </dd>
        </div>
      </dl>
    </div>
  );
}
